// How a "Réserver dans l'app" on padelr.app/coach/{username} reaches the app
// (hidden-beacon-3).
//
// Booking stays in the app: applying to a créneau may ask for a card, and the
// app is where the player's account and card are. The page only hands the
// créneau's id over, and there are two ways to do that, because the obvious
// one does not work from here.
//
// The obvious one is the universal link, https://padelr.app/app/lessons?item=.
// iOS and Android both claim /app/* for the app - but neither follows a
// universal link that is tapped on a page of the same domain, and this page is
// on padelr.app. Tapped here it would just load AppLinkView in the browser. So:
//
//   - on a phone, the button opens the custom scheme padelr://lessons?item=,
//     which the apps claim whatever page it is tapped on, and falls back to
//     the store badges when nothing took it;
//   - on a computer, the button shows the universal link as a QR code. A
//     phone's camera opening it is not a same-domain tap, so the app opens.
//
// Never put a day on either link. The apps tell a créneau link from the
// lesson reminders' links - which name a séance, not a créneau - by the day
// those always carry, so a day here would open the wrong screen.

export const SITE_ORIGIN = 'https://padelr.app'

export const APP_STORE_URL = 'https://apps.apple.com/fr/app/padelr/id6657992480'
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.padel.day.padelr'

// How long a phone has to leave the page before the fallback is shown. Long
// enough for iOS's "Open in Padelr?" prompt to be answered, short enough that
// somebody without the app is not left looking at nothing.
export const FALLBACK_MS = 2000

export function universalLessonLink(seriesId) {
    return `${SITE_ORIGIN}/app/lessons?item=${encodeURIComponent(seriesId)}`
}

export function schemeLessonLink(seriesId) {
    return `padelr://lessons?item=${encodeURIComponent(seriesId)}`
}

export function coachPageLink(username) {
    return `${SITE_ORIGIN}/coach/${encodeURIComponent(username)}`
}

// 'ios', 'android' or 'desktop'. An iPad asking for the desktop site reports
// itself as a Mac, and is told apart by having a touch screen.
export function platformOf(userAgent = '', maxTouchPoints = 0) {
    if (/android/i.test(userAgent)) return 'android'
    if (/iPad|iPhone|iPod/.test(userAgent)) return 'ios'
    if (/Macintosh/.test(userAgent) && maxTouchPoints > 1) return 'ios'
    return 'desktop'
}

// Opens the créneau in the app through the custom scheme, and calls
// onFallback when the page is still in front after FALLBACK_MS - which is
// what happens when no app claimed the scheme. Leaving the page (the app
// opening) cancels it.
//
// Returns a function that cancels the fallback, for a component unmounting.
export function openInApp(seriesId, onFallback, { win = window, doc = document, delay = FALLBACK_MS } = {}) {
    let timer = null

    const cancel = () => {
        if (timer !== null) {
            win.clearTimeout(timer)
            timer = null
        }
        doc.removeEventListener('visibilitychange', onHidden)
        win.removeEventListener('pagehide', cancel)
    }

    function onHidden() {
        if (doc.visibilityState === 'hidden') cancel()
    }

    doc.addEventListener('visibilitychange', onHidden)
    win.addEventListener('pagehide', cancel)

    timer = win.setTimeout(() => {
        timer = null
        cancel()
        if (doc.visibilityState !== 'hidden') onFallback()
    }, delay)

    win.location.href = schemeLessonLink(seriesId)

    return cancel
}
